import { AfterViewChecked, AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MESSAGECONSTANTS } from 'src/app/core/utils/constants';

// Company-page step 2 (Primary Contact) - see company-page.component.ts for the
// confirmed data-step -> TypeOfCompany mapping this component implements.
//
// Phone number fields use the existing native `angular-four-dot-five-phone-number`
// component (which already wraps intl-tel-input via the `angular-four-dot-five-phone-number` Angular component) instead of the legacy
// `ng-intl-tel-input` AngularJS directive used by company-page.html, since an
// AngularJS directive cannot run inside a downgraded Angular component's
// template. Validity/formatting behavior is unchanged - both wrap the same
// intl-tel-input library.
//
// Side-effecting actions that still depend on not-yet-ported services/modals
// (deleteCompanyContactModal via userService.getDeleteUserInfo) stay on the
// root legacy controller and are reached here only via output callbacks -
// same pattern as the not-yet-ported modals in later build steps.
@Component({
  selector: 'app-company-page-step-contact',
  templateUrl: './company-page-step-contact.component.html'
})
export class CompanyPageStepContactComponent implements AfterViewInit, AfterViewChecked, OnDestroy {

  @Input() typeOfCompany: string;
  @Input() isAssociatedClientUpdateInProgress: boolean;
  @Input() companyManagement: any = {};
  @Input() companyDetailsUpdateMode: boolean;
  @Input() readOnlyForClientOrBU: boolean;
  @Input() showRemoveButton: boolean;
  @Input() managers: any[] = [];
  @Input() phoneValidationStatuses: any[] = [];
  @Input() step2FormSubmitted: boolean;
  @Input() validateSecondaryDetails: boolean;

  @Output() removePrimaryContactCallback = new EventEmitter<any>();
  @Output() toggleSecondaryContactCallback = new EventEmitter<'add' | 'remove'>();
  @Output() addContactCallback = new EventEmitter<void>();
  @Output() removeContactCallback = new EventEmitter<number>();
  @Output() switchToContactOrHiringManagerCallback = new EventEmitter<{ type: string, contact: any }>();
  @Output() formStateChange = new EventEmitter<any>();

  @ViewChild('companyContactPersonDetailsForm') companyContactPersonDetailsForm: NgForm;
  @ViewChild('companySecondaryContactPersonDetailsForm') companySecondaryContactPersonDetailsForm: NgForm;
  @ViewChild('contactForm') contactForm: NgForm;

  MESSAGECONSTANTS = MESSAGECONSTANTS;

  isValidPrimaryWorkPhoneEntered = false;
  isValidPrimaryMobileEntered = false;
  isValidSecondaryWorkPhoneEntered = false;
  isValidSecondaryMobileEntered = false;

  primaryWorkPhoneFormat = '';
  primaryMobilePhoneFormat = '';
  secondaryWorkPhoneFormat = '';
  secondaryMobilePhoneFormat = '';
  contactWorkPhoneFormats: string[] = [];
  contactMobilePhoneFormats: string[] = [];

  // Keyed by ViewChild property name. companySecondaryContactPersonDetailsForm
  // (and contactForm, for additional contacts) don't exist in the DOM until
  // "Add Another"/"Add Contact" is clicked, so a one-time ngAfterViewInit
  // subscription (as used previously) misses their statusChanges entirely -
  // edits to those forms then never call emitFormState(), leaving the parent
  // with a stale $valid and silently blocking Next. ngAfterViewChecked
  // re-syncs subscriptions whenever a ViewChild's live reference changes
  // (including from undefined to a real NgForm) to catch that case.
  private formSubs: { [key: string]: Subscription } = {};
  private formRefs: { [key: string]: NgForm } = {};

  ngAfterViewInit() {
    setTimeout(() => this.emitFormState());
  }

  ngAfterViewChecked() {
    this.syncFormSubscription('companyContactPersonDetailsForm', this.companyContactPersonDetailsForm);
    this.syncFormSubscription('companySecondaryContactPersonDetailsForm', this.companySecondaryContactPersonDetailsForm);
    this.syncFormSubscription('contactForm', this.contactForm);
  }

  private syncFormSubscription(key: string, form: NgForm) {
    if (this.formRefs[key] === form) {
      return;
    }
    this.formRefs[key] = form;
    if (this.formSubs[key]) {
      this.formSubs[key].unsubscribe();
      delete this.formSubs[key];
    }
    if (form) {
      this.formSubs[key] = form.statusChanges.subscribe(() => this.emitFormState());
    }
  }

  ngOnDestroy() {
    Object.keys(this.formSubs).forEach(key => this.formSubs[key].unsubscribe());
  }

  get secondaryContact() {
    return this.companyManagement.contactList[1];
  }

  get additionalContacts() {
    return this.companyManagement.contactList.slice(2);
  }

  get addAnotherLabel() {
    return this.typeOfCompany === 'Client' && !this.isAssociatedClientUpdateInProgress
      ? 'Add Another' : 'Add Secondary Contact';
  }

  get removeSecondaryLabel() {
    return this.typeOfCompany === 'Client' && !this.isAssociatedClientUpdateInProgress
      ? 'Remove Contact' : 'Remove Secondary Contact';
  }

  get showContactTypeRadios() {
    return this.typeOfCompany === 'BusinessUnit' ||
      (this.typeOfCompany === 'Client' && !this.isAssociatedClientUpdateInProgress);
  }

  onRemovePrimaryContact() {
    this.removePrimaryContactCallback.emit(this.companyManagement.contactList[0]);
  }

  onToggleSecondaryContact(action: 'add' | 'remove') {
    this.toggleSecondaryContactCallback.emit(action);
  }

  onAddContact() {
    this.addContactCallback.emit();
  }

  onRemoveContact(index: number) {
    this.removeContactCallback.emit(index);
  }

  onSwitchToContactOrHiringManager(type: string, contact: any) {
    this.switchToContactOrHiringManagerCallback.emit({ type, contact });
  }

  onPrimaryWorkPhoneInvalid(invalid: boolean) {
    this.isValidPrimaryWorkPhoneEntered = !invalid;
    this.emitFormState();
  }

  onPrimaryMobilePhoneInvalid(invalid: boolean) {
    this.isValidPrimaryMobileEntered = !invalid;
    this.emitFormState();
  }

  onSecondaryWorkPhoneInvalid(invalid: boolean) {
    this.isValidSecondaryWorkPhoneEntered = !invalid;
    this.emitFormState();
  }

  onSecondaryMobilePhoneInvalid(invalid: boolean) {
    this.isValidSecondaryMobileEntered = !invalid;
    this.emitFormState();
  }

  onContactWorkPhoneInvalid(status: any, invalid: boolean) {
    status.isValidWorkNumber = !invalid;
    status.invalidWorkNumber = invalid;
    this.emitFormState();
  }

  onContactMobilePhoneInvalid(status: any, invalid: boolean) {
    status.isValidMobileNumber = !invalid;
    status.invalidMobileNumber = invalid;
    this.emitFormState();
  }

  private emitFormState() {
    const asFormState = (form: NgForm) => form ? {
      $valid: form.valid,
      $dirty: form.dirty,
      $error: { required: form.invalid },
      $setPristine: () => form.form.markAsPristine()
    } : { $valid: true, $dirty: false, $error: {}, $setPristine: () => { } };

    this.formStateChange.emit({
      companyContactPersonDetailsForm: asFormState(this.companyContactPersonDetailsForm),
      companySecondaryContactPersonDetailsForm: asFormState(this.companySecondaryContactPersonDetailsForm),
      contactForm: asFormState(this.contactForm),
      isValidPrimaryWorkPhoneEntered: this.isValidPrimaryWorkPhoneEntered,
      isValidPrimaryMobileEntered: this.isValidPrimaryMobileEntered,
      isValidSecondaryWorkPhoneEntered: this.isValidSecondaryWorkPhoneEntered,
      isValidSecondaryMobileEntered: this.isValidSecondaryMobileEntered
    });
  }
}
